/**
 * Section-view (cutaway) domain helpers — pure, framework-light logic
 * for the clipping-plane section mode (issue 079).
 *
 * Two concerns live here, both unit-testable without a GPU:
 *
 *  1. **Layer classification.** Every clippable sauna mesh is mapped to a
 *     material *family* (panel / framing / exterior / …) by the identity of
 *     its shared material proxy from `engines/materials.ts`. Because
 *     environment materials (`matGround`, grass, dressing, sky) are NOT in
 *     this map, classification doubles as the "only clip the sauna body,
 *     never mark/dressing/grass" gate — no coupling to SaunaModel required.
 *     This composes with layer ownership rather than seizing geometry, per
 *     docs/notes/2026-03-29-front-cutaways-must-compose-with-layer-ownership.md.
 *
 *  2. **Clip-plane math.** Maps a normalized 0..1 slider position to a
 *     three.js clipping-plane `{ normal, constant }` plus the world coord
 *     of the cut (used to place the scene drag handle and the cap quads).
 */
import { snapRotationDeg } from "../../domain/viewer/saunaRotation";
import {
	matAsphaltBoard,
	matBand,
	matBarrelEndCap,
	matBench,
	matBenchPhysical,
	matCeilingPanel,
	matCeilingPanelPhysical,
	matChimney,
	matConcrete,
	matCornerTrim,
	matCornerTrimHorizontal,
	matCradle,
	matDeckBoard,
	matDivider,
	matDoorHandle,
	matDoorWood,
	matEndCap,
	matExteriorCladding,
	matFascia,
	matFasciaHorizontal,
	matFireGlass,
	matFireGlassBudget,
	matGlass,
	matGlassBudget,
	matHeater,
	matHeaterDoor,
	matHeaterElectric,
	matHeaterStone,
	matInsulation,
	matMetal,
	matPanel,
	matPanelPhysical,
	matRoofFelt,
	matRoofFlashing,
	matRoofSheetMetal,
	matRoofTop,
	matRoofUnderlay,
	matShield,
	matStave,
	matTreatedWood,
	matVaporBarrier,
	matVentExhaustFace,
	matVentSupplyFace,
	matVentThroat,
	matWindBarrier,
	matWood,
} from "../../engines/materials";
import type { Dictionary } from "../../i18n/types";

export type SectionAxis = "x" | "z";
export const SECTION_AXES: readonly SectionAxis[] = ["x", "z"] as const;

/**
 * Material families shown in the cut. Order is the legend order. Each
 * family carries the cap colour used by the stencil caps AND the swatch
 * in the HUD legend, plus a fill pattern hint (insulation-style families
 * use diagonal hatching — kept here for forward-compat even though the
 * current geometry has no discrete insulation mesh).
 */
export type SectionLayerFamily =
	| "exterior"
	| "framing"
	| "panel"
	| "foundation"
	| "roof"
	| "bench"
	| "glass"
	| "insulation";

export interface SectionLayerDefinition {
	family: SectionLayerFamily;
	/** Cap fill colour (hex int) — matches the HUD legend swatch. */
	capColor: number;
	/** Cross-hatch the cap instead of a solid fill (insulation-style). */
	pattern: "solid" | "diagonal";
	/** i18n key resolving to the human-readable layer name. */
	legendKey: keyof Dictionary;
}

/**
 * Ordered family table. Colours intentionally echo the project palette /
 * the existing visibility-bar vocabulary so the section reads as the same
 * design language (terracotta facade, forest-green framing, cream panel).
 */
export const SECTION_LAYER_DEFINITIONS: readonly SectionLayerDefinition[] = [
	{
		family: "exterior",
		capColor: 0xc45c3a,
		pattern: "solid",
		legendKey: "viewer_sectionLayerExterior",
	},
	{
		family: "framing",
		capColor: 0x5a6b50,
		pattern: "solid",
		legendKey: "viewer_sectionLayerFraming",
	},
	{
		family: "panel",
		capColor: 0xe4c9a7,
		pattern: "solid",
		legendKey: "viewer_sectionLayerPanel",
	},
	{
		family: "foundation",
		capColor: 0xa8afb6,
		pattern: "solid",
		legendKey: "viewer_sectionLayerFoundation",
	},
	{
		family: "roof",
		capColor: 0x3a3d42,
		pattern: "solid",
		legendKey: "viewer_sectionLayerRoof",
	},
	{
		family: "bench",
		capColor: 0xcaa06a,
		pattern: "solid",
		legendKey: "viewer_sectionLayerBench",
	},
	{
		family: "glass",
		capColor: 0x9cc4e8,
		pattern: "solid",
		legendKey: "viewer_sectionLayerGlass",
	},
	{
		family: "insulation",
		capColor: 0xc9a86a,
		pattern: "diagonal",
		legendKey: "viewer_sectionLayerInsulation",
	},
];

const FAMILY_BY_NAME = new Map<SectionLayerFamily, SectionLayerDefinition>(
	SECTION_LAYER_DEFINITIONS.map((def) => [def.family, def]),
);

export function getSectionLayerDefinition(
	family: SectionLayerFamily,
): SectionLayerDefinition {
	const def = FAMILY_BY_NAME.get(family);
	if (!def) {
		throw new Error(`Unknown section layer family: ${family}`);
	}
	return def;
}

/**
 * Material-proxy → family map. The values are the lazy material proxies
 * exported from the material library; engines assign these exact proxies
 * to meshes, so reference-identity lookup (`MATERIAL_FAMILY.get(material)`)
 * resolves a mesh's family. Materials that read as the same layer (e.g.
 * standard + physical panel variants, or both glass quality tiers) map to
 * the same family so a runtime tier swap never changes a cap colour.
 *
 * Anything NOT in this map (ground, grass, dressing, guides, analysis
 * overlays) is treated as non-clippable and is left untouched.
 */
const MATERIAL_FAMILY: ReadonlyMap<unknown, SectionLayerFamily> = new Map<
	unknown,
	SectionLayerFamily
>([
	// Exterior cladding / trim / door leaf / barrel staves
	[matExteriorCladding, "exterior"],
	[matCornerTrim, "exterior"],
	[matCornerTrimHorizontal, "exterior"],
	[matFascia, "exterior"],
	[matFasciaHorizontal, "exterior"],
	[matDoorWood, "exterior"],
	[matStave, "exterior"],
	// The barrel's gable discs are facade; `matEndCap` (its interior floor) is not.
	[matBarrelEndCap, "exterior"],
	[matDeckBoard, "exterior"],
	[matWindBarrier, "exterior"],
	// Vent hardware (tallriksventiler) penetrates the wall build-up, so the
	// cut has to include it — the NEUTRAL faces and the shadow-gap throat.
	// The blue/red `matVentIn`/`matVentOut` diagram faces stay out of this map
	// on purpose: they belong to the ventilation analysis overlay, and overlays
	// are never clipped (same rule as the heater-clearance envelope).
	[matVentSupplyFace, "exterior"],
	[matVentExhaustFace, "exterior"],
	[matVentThroat, "exterior"],
	// Timber framing (studs, plates, treated sills, cradles, end caps)
	[matWood, "framing"],
	[matTreatedWood, "framing"],
	// Trossbottenskiva (12 mm asfaboard) — part of the golvbjälklag, so it
	// caps with the joists and läkten it is carried by rather than getting a
	// legend row of its own for a layer nobody reads as a separate family.
	[matAsphaltBoard, "framing"],
	[matEndCap, "framing"],
	[matCradle, "framing"],
	// Thermal envelope: stenullsisolering in the bays + bastufolie over them.
	// Hatched in the cut, the way an insulation layer reads on a section drawing.
	[matInsulation, "insulation"],
	[matVaporBarrier, "insulation"],
	// Interior cladding / divider panelling
	[matPanel, "panel"],
	[matCeilingPanel, "panel"],
	[matCeilingPanelPhysical, "panel"],
	[matPanelPhysical, "panel"],
	[matDivider, "panel"],
	// Foundation piers / plinths
	[matConcrete, "foundation"],
	// Roof assembly (boards, underlay, felt, sheet metal, flashings, bands)
	[matRoofTop, "roof"],
	[matRoofUnderlay, "roof"],
	[matRoofFelt, "roof"],
	[matRoofSheetMetal, "roof"],
	[matRoofFlashing, "roof"],
	[matBand, "roof"],
	// Benches
	[matBench, "bench"],
	[matBenchPhysical, "bench"],
	// Glazing (both quality tiers + heater fire glass)
	[matGlass, "glass"],
	[matGlassBudget, "glass"],
	[matFireGlass, "glass"],
	[matFireGlassBudget, "glass"],
	// Hardware / heater / chimney — fold into framing so the cut still
	// caps them rather than leaving a hollow hole, without adding legend
	// noise for tiny incidental meshes.
	[matMetal, "framing"],
	[matDoorHandle, "framing"],
	[matShield, "framing"],
	[matHeater, "framing"],
	[matHeaterStone, "framing"],
	[matHeaterDoor, "framing"],
	[matHeaterElectric, "framing"],
	[matChimney, "framing"],
]);

/**
 * Resolve the section family for a single material instance (or the first
 * entry of a material array). Returns null when the material is not a
 * known sauna-body material — i.e. it should not be clipped or capped.
 */
export function classifyMaterialFamily(
	material: unknown,
): SectionLayerFamily | null {
	if (Array.isArray(material)) {
		for (const entry of material) {
			const family = MATERIAL_FAMILY.get(entry);
			if (family) return family;
		}
		return null;
	}
	return MATERIAL_FAMILY.get(material) ?? null;
}

/** True when a material belongs to the clippable sauna body. */
export function isSectionClippableMaterial(material: unknown): boolean {
	return classifyMaterialFamily(material) !== null;
}

/**
 * Legend entries for the families actually present in the current cut,
 * preserving the canonical family order. Driving the legend off the
 * families found in the scene keeps colour→name mapping honest: a cut
 * through a glassless wall won't advertise a "Glass" swatch.
 */
export function resolveSectionLegendEntries(
	presentFamilies: Iterable<SectionLayerFamily>,
): SectionLayerDefinition[] {
	const present = new Set(presentFamilies);
	return SECTION_LAYER_DEFINITIONS.filter((def) => present.has(def.family));
}

// ─── Clip-plane math ─────────────────────────────────────────

interface Vec3Like {
	x: number;
	y: number;
	z: number;
}

export interface SectionBounds {
	min: Vec3Like;
	max: Vec3Like;
}

export interface SectionPlaneParams {
	/** Plane normal as a tuple — three.js keeps the half-space where
	 * `normal·p + constant >= 0`; the opposite half is clipped away. */
	normal: [number, number, number];
	constant: number;
	/** World coordinate of the cut along `axis` (handle / cap placement). */
	coord: number;
}

function clamp01(value: number): number {
	if (Number.isNaN(value)) return 0.5;
	return Math.min(1, Math.max(0, value));
}

/**
 * Map a 0..1 slider position to a clipping plane perpendicular to `axis`.
 *
 * `position01 = 0` cuts at the axis minimum, `1` at the maximum. By
 * default the half-space on the positive-axis side of the cut is removed
 * (so dragging the slider sweeps the visible solid from max toward min);
 * `flip` removes the opposite side instead.
 */
export interface WorldSectionAxis {
	/** The world axis the clip plane is aligned to. */
	axis: SectionAxis;
	/**
	 * True when the model axis points down the NEGATIVE world axis, so the
	 * cut's direction of travel is reversed: `position01` has to be mirrored
	 * and `flip` inverted, or the slider sweeps backwards and the half that
	 * survives is the one the user asked to remove.
	 */
	inverted: boolean;
}

/**
 * Map the UI's MODEL-space cut direction onto the world axis *and direction*
 * the clip plane actually uses.
 *
 * `SectionAxis` is authored from the user's point of view — the buttons say
 * "across the width" and "across the length" — but the clipping plane is
 * world-axis-aligned. Turning the building changes two things, and only one
 * of them is obvious: a quarter turn swaps which world axis carries the
 * model's width, and *every* turn except 0° reverses at least one axis.
 *
 * The reversal is the one that bites: at 180° both model axes point down the
 * negative world axis, so `position01 = 0` cuts at the far end and `flip`
 * keeps the half the user asked to remove.
 *
 * Model +X and +Z under `rotation.y`, by quarter turn:
 *
 * | deg | model +X → | model +Z → |
 * |-----|-----------|-----------|
 * | 0   | +x        | +z        |
 * | 90  | -z        | +x        |
 * | 180 | -x        | -z        |
 * | 270 | +z        | -x        |
 */
export function resolveWorldSectionAxis(
	axis: SectionAxis,
	rotationDeg: number,
): WorldSectionAxis {
	switch (snapRotationDeg(rotationDeg)) {
		case 90:
			return axis === "x"
				? { axis: "z", inverted: true }
				: { axis: "x", inverted: false };
		case 180:
			return { axis, inverted: true };
		case 270:
			return axis === "x"
				? { axis: "z", inverted: false }
				: { axis: "x", inverted: true };
		default:
			return { axis, inverted: false };
	}
}

export function resolveSectionPlaneParams(
	axis: SectionAxis,
	position01: number,
	bounds: SectionBounds,
	flip = false,
): SectionPlaneParams {
	const min = bounds.min[axis];
	const max = bounds.max[axis];
	const coord = min + (max - min) * clamp01(position01);
	// flip=false → keep p[axis] <= coord (normal = -axis, constant = coord)
	// flip=true  → keep p[axis] >= coord (normal = +axis, constant = -coord)
	const sign = flip ? 1 : -1;
	const normal: [number, number, number] =
		axis === "x" ? [sign, 0, 0] : [0, 0, sign];
	const constant = flip ? -coord : coord;
	return { normal, constant, coord };
}
